import CloseIcon from '@mui/icons-material/Close';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import OpenInFullIcon from '@mui/icons-material/OpenInFull';
import {
  Box,
  Button,
  Divider,
  Drawer,
  IconButton,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import type { ReactNode } from 'react';

import { pageBandColor } from '~/theme';

import { ROW_PREVIEW_PANEL_ATTRIBUTE, type RowPreview } from './useRowPreview';

const ROW_PREVIEW_PANEL_WIDTH = 600;

// Tables inside a detail page open records in a new tab, so the page stays put.
export const openInNewTab = (href: string) => {
  window.open(href, '_blank', 'noopener');
};

const HINT = 'Space / Switch toggles Peek mode.\nHover / arrows ↑ ↓ / buttons move between rows.';

interface RowPreviewPanelProps {
  rowPreview: RowPreview;
  ariaLabel: string;
  // Omitted for records with no page of their own.
  onOpenFullPage?: (id: string) => void;
  hasFullPage?: (id: string) => boolean;
  children: (id: string) => ReactNode;
}

export function RowPreviewPanel({
  rowPreview,
  ariaLabel,
  onOpenFullPage,
  hasFullPage,
  children,
}: RowPreviewPanelProps) {
  const { previewId, close, step, getNeighbor, pin, panelHoverHandlers } = rowPreview;
  const hasPrevious = getNeighbor(previewId, -1) !== null;
  const hasNext = getNeighbor(previewId, 1) !== null;

  return (
    <Drawer
      anchor="right"
      variant="persistent"
      open={Boolean(previewId)}
      slotProps={{
        paper: {
          ...panelHoverHandlers,
          onPointerDownCapture: pin,
          'aria-label': ariaLabel,
          [ROW_PREVIEW_PANEL_ATTRIBUTE]: '',
          sx: {
            width: { xs: '100vw', md: ROW_PREVIEW_PANEL_WIDTH },
            display: 'flex',
            flexDirection: 'column',
            boxShadow: 8,
          },
        } as object,
      }}
    >
      <Stack direction="row" alignItems="center" spacing={1} sx={{ px: 2, py: 1 }}>
        <Box flex={1} />
        {onOpenFullPage && (!previewId || !hasFullPage || hasFullPage(previewId)) ? (
          <Button
            size="small"
            startIcon={<OpenInFullIcon fontSize="small" />}
            disabled={!previewId}
            onClick={() => {
              if (!previewId) return;
              close();
              onOpenFullPage(previewId);
            }}
          >
            Open full page
          </Button>
        ) : null}
        <Tooltip title="Close (Esc)">
          <IconButton size="small" aria-label="Close preview" onClick={close}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Stack>
      <Divider />
      <Box flex={1} minHeight={0} sx={{ overflowY: 'auto', bgcolor: pageBandColor }}>
        {previewId ? children(previewId) : null}
      </Box>
      <Divider />
      <Stack direction="row" alignItems="center" spacing={1} sx={{ px: 2, py: 1 }}>
        <Typography
          variant="caption"
          color="text.secondary"
          flex={1}
          sx={{ whiteSpace: 'pre-line' }}
        >
          {HINT}
        </Typography>
        <Tooltip title="Previous row (↑)">
          <span>
            <Button
              size="small"
              variant="outlined"
              startIcon={<KeyboardArrowUpIcon />}
              disabled={!hasPrevious}
              onClick={() => step(-1)}
            >
              Previous
            </Button>
          </span>
        </Tooltip>
        <Tooltip title="Next row (↓)">
          <span>
            <Button
              size="small"
              variant="outlined"
              endIcon={<KeyboardArrowDownIcon />}
              disabled={!hasNext}
              onClick={() => step(1)}
            >
              Next
            </Button>
          </span>
        </Tooltip>
      </Stack>
    </Drawer>
  );
}
